import { formatDayMonth, formatHours, formatMonth, formatWeekday, t } from '@glory/i18n';
import { type Minutes, minutes } from '@glory/kernel';
import {
  CALENDAR_FIRST_YEAR,
  CALENDAR_LAST_YEAR,
  calendarEntityId,
  type DayType,
  type IsoDate,
  isCalendarYear,
  type MonthNorm,
  yearNorms,
} from '@glory/org/domain';
import {
  Badge,
  Callout,
  Card,
  cn,
  ContentSwap,
  Dialog,
  Fact,
  IconButton,
  InfoHint,
  PageHeader,
  SettingsSection,
  SkeletonRows,
} from '@glory/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';

import { EntityHistory } from '../../../features/audit/entity-history.tsx';
import { DayDialog, describeDay } from '../../../features/calendar/day-dialog.tsx';
import { type CalendarRow, yearModel, type YearModel } from '../../../features/calendar/model.ts';
import { CalendarLegend, TYPE_LABEL, YearGrid } from '../../../features/calendar/year-grid.tsx';
import { orpc } from '../../../lib/orpc.ts';
import { usePermissions } from '../../../lib/permissions.ts';

interface CalendarSearch {
  readonly year?: number | undefined;
}

export const Route = createFileRoute('/_app/settings/calendar')({
  validateSearch: (search: Record<string, unknown>): CalendarSearch => {
    const year = Number(search.year);
    return { year: isCalendarYear(year) ? year : undefined };
  },
  component: CalendarPage,
});

const TYPE_TONE = { work: 'neutral', short: 'warn', off: 'danger' } as const satisfies Record<
  DayType,
  string
>;

const hours = (value: Minutes) => t('units.hoursShort', { value: formatHours(value) });

function CalendarPage() {
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const year = search.year ?? new Date().getFullYear();
  const { can } = usePermissions();
  const canManage = can('calendar.manage');
  const calendar = useQuery({
    ...orpc.org.calendar.list.queryOptions({ input: { year } }),
    placeholderData: keepPreviousData,
  });
  const [editing, setEditing] = useState<IsoDate | null>(null);
  const data = calendar.data;
  const model = useMemo(() => (data ? yearModel(data.year, data.days) : null), [data]);
  const summary = useMemo(() => (model ? yearSummary(model) : null), [model]);
  const pick = canManage ? setEditing : null;
  const currentMonth =
    data?.today.startsWith(`${String(data.year)}-`) === true ? Number(data.today.slice(5, 7)) : null;

  // Что под заголовком: сбой, загрузка, календарь — они проявляются друг из друга, без скачка высоты.
  const phase = calendar.isError ? 'error' : !model || !summary || !data ? 'loading' : 'ready';

  return (
    <div className="flex flex-col gap-4 px-4 py-5 md:px-6">
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2 whitespace-normal">
            {t('calendar.title')}
            <InfoHint hint={{ text: t('calendar.hint'), example: t('calendar.hintExample') }} />
          </span>
        }
        actions={
          <YearSwitcher
            year={year}
            onChange={(next) => {
              void navigate({ search: { year: next }, replace: true });
            }}
          />
        }
      />
      <p className="-mt-2 text-muted">{t('calendar.lead')}</p>

      <ContentSwap swapKey={phase}>
        {calendar.isError ? (
          <Callout>{calendar.error.message}</Callout>
        ) : !model || !summary || !data ? (
          <SkeletonRows rows={8} />
        ) : (
          <div
            aria-busy={calendar.isPlaceholderData}
            className={cn(
              'flex flex-col gap-4 transition-opacity',
              calendar.isPlaceholderData && 'opacity-60',
            )}
          >
            {model.decreed ? null : (
              <Card className="px-4 py-3 text-small text-muted">
                {t('calendar.noOfficial', { year: String(model.year) })}
              </Card>
            )}
            <YearFacts total={summary.total} />
            <Card className="flex flex-col gap-4 p-4">
              <CalendarLegend />
              <YearGrid model={model} today={data.today} {...(pick ? { onPick: pick } : {})} />
              {canManage ? <p className="text-caption text-muted">{t('calendar.editHint')}</p> : null}
            </Card>
            <SettingsSection title={t('calendar.exceptionsTitle')} lead={t('calendar.exceptionsLead')}>
              {model.rows.length === 0 ? (
                <p className="text-small text-muted">{t('calendar.exceptionsEmpty')}</p>
              ) : (
                <ul className="-my-1.5 flex flex-col divide-y divide-border-soft">
                  {model.rows.map((row) => (
                    <ExceptionRow key={row.date} row={row} decreed={model.decreed} onPick={pick} />
                  ))}
                </ul>
              )}
            </SettingsSection>
            <NormsTable months={summary.months} total={summary.total} currentMonth={currentMonth} />
            <SettingsSection title={t('calendar.historyTitle')} lead={t('calendar.historyLead')}>
              <EntityHistory key={model.year} entityType="calendar" entityId={calendarEntityId(model.year)} />
            </SettingsSection>
          </div>
        )}
      </ContentSwap>

      <Dialog
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
      >
        {editing !== null && model ? (
          <DayDialog
            date={editing}
            model={model}
            onDone={() => {
              setEditing(null);
            }}
          />
        ) : null}
      </Dialog>
    </div>
  );
}

function YearSwitcher({ year, onChange }: { year: number; onChange: (year: number) => void }) {
  return (
    <div role="group" aria-label={t('calendar.year')} className="flex items-center gap-1">
      <IconButton
        label={t('calendar.prevYear')}
        icon={<ChevronLeft />}
        disabled={year <= CALENDAR_FIRST_YEAR}
        onClick={() => {
          onChange(year - 1);
        }}
      />
      <span
        aria-live="polite"
        className="min-w-[4.5ch] text-center font-display text-title font-bold tabular-nums"
      >
        {year}
      </span>
      <IconButton
        label={t('calendar.nextYear')}
        icon={<ChevronRight />}
        disabled={year >= CALENDAR_LAST_YEAR}
        onClick={() => {
          onChange(year + 1);
        }}
      />
    </div>
  );
}

type Totals = Omit<MonthNorm, 'month'>;

/** Нормы по месяцам и итог за год. */
function yearSummary(model: YearModel): { months: MonthNorm[]; total: Totals } {
  const months = yearNorms(model.year, model.exceptions);
  const total = months.reduce<Totals>(
    (sum, month) => ({
      workingDays: sum.workingDays + month.workingDays,
      daysOff: sum.daysOff + month.daysOff,
      minutes: minutes(sum.minutes + month.minutes),
    }),
    { workingDays: 0, daysOff: 0, minutes: minutes(0) },
  );
  return { months, total };
}

function YearFacts({ total }: { total: Totals }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      <Fact label={t('calendar.workingDays')} value={total.workingDays} />
      <Fact label={t('calendar.daysOff')} value={total.daysOff} />
      <Fact
        label={
          <span className="inline-flex items-center gap-1">
            {t('calendar.norm')}
            <InfoHint hint={{ text: t('calendar.normHint') }} />
          </span>
        }
        value={hours(total.minutes)}
      />
    </div>
  );
}

function ExceptionRow({
  row,
  decreed,
  onPick,
}: {
  row: CalendarRow;
  decreed: boolean;
  onPick: ((date: IsoDate) => void) | null;
}) {
  const content = (
    <>
      <span className="font-semibold tabular-nums">{formatDayMonth(row.date)}</span>
      <span className="text-muted">{formatWeekday(row.date)}</span>
      <Badge tone={TYPE_TONE[row.state.type]} className="justify-self-start">
        {TYPE_LABEL[row.state.type]}
      </Badge>
      <span className="col-span-full flex min-w-0 flex-col gap-0.5 sm:col-span-1">
        {row.state.note}
        {row.changed ? (
          <span className="flex flex-wrap items-center gap-x-1.5 text-caption text-muted">
            <Badge tone="strong">{t('calendar.changed')}</Badge>
            {decreed ? t('calendar.official', { type: describeDay(row.official, true) }) : null}
          </span>
        ) : null}
      </span>
    </>
  );
  const grid =
    'grid w-full grid-cols-[6.5rem_1.75rem_auto] items-baseline gap-x-2 gap-y-0.5 py-2 text-left sm:grid-cols-[6.5rem_1.75rem_7.5rem_minmax(0,1fr)]';
  return (
    <li>
      {onPick ? (
        <button
          type="button"
          className={cn(
            grid,
            '-mx-2 w-[calc(100%+1rem)] rounded-sm px-2 hover:bg-surface-2 focus-visible:ring-2 focus-visible:ring-focus focus-visible:outline-none',
          )}
          onClick={() => {
            onPick(row.date);
          }}
        >
          {content}
        </button>
      ) : (
        <div className={grid}>{content}</div>
      )}
    </li>
  );
}

function NormsTable({
  months,
  total,
  currentMonth,
}: {
  months: readonly MonthNorm[];
  total: Totals;
  currentMonth: number | null;
}) {
  const cell = 'py-1.5 pl-3 text-right tabular-nums';
  return (
    <SettingsSection title={t('calendar.normsTitle')} lead={t('calendar.normsLead')}>
      <table className="w-full text-small">
        <thead>
          <tr className="text-caption text-muted">
            <th className="pb-1 text-left font-bold">{t('calendar.columnMonth')}</th>
            <th className="pb-1 pl-3 text-right font-bold">{t('calendar.columnWorking')}</th>
            <th className="pb-1 pl-3 text-right font-bold">{t('calendar.columnOff')}</th>
            <th className="pb-1 pl-3 text-right font-bold">{t('calendar.columnHours')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border-soft border-t border-border-soft">
          {months.map((norm) => (
            <tr key={norm.month} className={cn(norm.month === currentMonth && 'bg-accent/15')}>
              <td className="py-1.5 font-semibold">{formatMonth(norm.month)}</td>
              <td className={cell}>{norm.workingDays}</td>
              <td className={cell}>{norm.daysOff}</td>
              <td className={cell}>{formatHours(norm.minutes)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-[1.5px] border-border font-bold">
            <td className="py-1.5">{t('calendar.total')}</td>
            <td className={cell}>{total.workingDays}</td>
            <td className={cell}>{total.daysOff}</td>
            <td className={cell}>{formatHours(total.minutes)}</td>
          </tr>
        </tfoot>
      </table>
    </SettingsSection>
  );
}
